import { type MistakeAnalysisData } from "components/Resume/MistakeAnalysisContext";
import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";

/**
 * Transform ResumeAnalysis data into MistakeAnalysisData format
 * Extracts all mistake arrays from analysis for persistent highlighting
 */
export function transformAnalysisToMistakeData(analysis: ResumeAnalysis | null): MistakeAnalysisData {
  if (!analysis) {
    // Return empty structure if no analysis available
    return {
      profile: null,
      experience: {},
      education: {},
      projects: {},
      skills: null,
      overall: {
        repeatedActionVerbs: [],
        repeatedActionVerbsWithCounts: [],
        repeatedSentences: [],
      },
    };
  }

  // Transform Profile/Personal section
  const profileData = analysis.profile.summaryAnalysis.statement
    ? {
        words: {
          spellingMistakes: [
            ...new Set(
              analysis.profile.summaryAnalysis.analysis.spellingMistakes
                ?.map((m) => m.incorrect)
                .filter(Boolean) || []
            ),
          ],
          grammarMistakes: [
            ...new Set(
              analysis.profile.summaryAnalysis.analysis.grammarMistakes
                ?.map((m) => m.incorrect)
                .filter(Boolean) || []
            ),
          ],
          buzzWords: [
            ...new Set(analysis.profile.summaryAnalysis.analysis.buzzWords?.filter(Boolean) || []),
          ],
          repeatedWords: (() => {
            const statement = (analysis.profile.summaryAnalysis.statement || "").toString();
            if (!statement) return [];
            const stopwords = new Set([
              "the", "and", "to", "of", "a", "in", "for", "with", "on", "at", "by", "is", "are",
              "was", "were", "be", "or", "that", "this", "it", "as", "an", "from", "have", "has",
              "had", "i", "my", "me", "we", "our", "you", "your", "but", "not", "will", "can",
              "would", "should",
            ]);
            const cleaned = statement.replace(/[^A-Za-z0-9\s'-]+/g, ' ');
            const tokens = cleaned
              .toLowerCase()
              .split(/\s+/)
              .map((t) => t.replace(/^['-]+|['-]+$/g, ''))
              .filter((t) => t.length >= 3 && !stopwords.has(t));
            const counts: Record<string, number> = {};
            for (const t of tokens) {
              counts[t] = (counts[t] || 0) + 1;
            }
            return Object.keys(counts).filter((w) => counts[w] > 1);
          })(),
          personalPronouns: [
            ...new Set(
              analysis.profile.summaryAnalysis.analysis.personalPronouns?.filter(Boolean) || []
            ),
          ],
          actionVerbIssues: [],
        },
        sentences: {
          longSentences: (() => {
            const statement = analysis.profile.summaryAnalysis.statement;
            if (!statement) return [];
            const wordCount = statement.trim().split(/\s+/).length;
            return wordCount > 25 ? [statement] : [];
          })(),
          doesNotEndsWithPeriod: [],
          // doesNotEndsWithPeriod: (() => {
          //   if (analysis.profile.summaryAnalysis.analysis.endsWithPeriod === 0) {
          //     return [analysis.profile.summaryAnalysis.statement];
          //   }
          //   return [];
          // })(),
          repeatedPhrases: [],
        },
        hasPassiveVoice: analysis.profile.summaryAnalysis.analysis.isPassiveVoice === 1,
      }
    : null;

  // Transform Work Experience sections
  const experienceData: Record<number, any> = {};
  analysis.workExperiences.individualWorkExperiencesAnalysis.forEach((exp, index) => {
    experienceData[index] = {
      words: {
        spellingMistakes: [
          ...new Set(
            exp.descriptionAnalysis
              .flatMap((point) =>
                point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
              )
              .filter(Boolean)
          ),
        ],
        grammarMistakes: [
          ...new Set(
            exp.descriptionAnalysis
              .flatMap((point) =>
                point.analysis.grammarMistakes.map((m) => m.incorrect)
              )
              .filter(Boolean)
          ),
        ],
        buzzWords: [
          ...new Set(
            exp.descriptionAnalysis
              .flatMap((point) => point.analysis.buzzWords)
              .filter(Boolean)
          ),
        ],
        repeatedWords: (() => {
          const verbs: string[] = exp.descriptionAnalysis.flatMap((d) =>
            d.analysis.actionVerbAnalysis.map((v) => v.verb)
          );
          const counts: Record<string, number> = {};
          verbs.forEach((v) => {
            const key = (v || "").trim().toLowerCase();
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
          });
          return Object.keys(counts)
            .filter((k) => counts[k] > 1)
            .map((k) => k);
        })(),
        personalPronouns: [
          ...new Set(
            exp.descriptionAnalysis
              .flatMap((point) => point.analysis.personalPronouns)
              .filter(Boolean)
          ),
        ],
        actionVerbIssues: [
          ...new Set(
            exp.descriptionAnalysis.flatMap((desc) => {
              return desc.analysis.actionVerbAnalysis
                .filter((verb) => {
                  if (verb.isPastTense !== 0) return false;
                  const statement = desc.statement.trim();
                  const firstWord = statement.split(" ")[0].toLowerCase().replace(/[^\w]/g, "");
                  return firstWord === verb.verb.toLowerCase();
                })
                .map((verb) => verb.verb);
            })
          ),
        ],
      },
      sentences: {
        longSentences: [
          ...new Set(
            exp.descriptionAnalysis
              .filter((desc) => {
                const wordCount = desc.statement.trim().split(/\s+/).length;
                return wordCount > 25;
              })
              .map((desc) => desc.statement)
          ),
        ],
        doesNotEndsWithPeriod: [
          ...new Set(
            exp.descriptionAnalysis
              .filter((desc) => desc.analysis.endsWithPeriod === 0)
              .map((desc) => desc.statement)
          ),
        ],
        repeatedPhrases: [],
        // repeatedPhrases: (() => {
        //   const shingles: string[] = [];
        //   const normalize = (s: string) =>
        //     s
        //       .trim()
        //       .toLowerCase()
        //       .replace(/[^a-z0-9\s]/g, " ")
        //       .replace(/\s+/g, " ");
        //   exp.descriptionAnalysis.forEach((d) => {
        //     const text = normalize(d.statement || "");
        //     if (!text) return;
        //     const words = text.split(" ").filter(Boolean);
        //     const n = 3;
        //     for (let i = 0; i + n <= words.length; i++) {
        //       shingles.push(words.slice(i, i + n).join(" "));
        //     }
        //   });
        //   const counts: Record<string, number> = {};
        //   shingles.forEach((s) => (counts[s] = (counts[s] || 0) + 1));
        //   return Object.keys(counts).filter((k) => counts[k] > 1);
        // })(),
      },
      // hasPassiveVoice: exp.descriptionAnalysis.some(
      //   (desc) => desc.analysis.isPassiveVoice === 1
      // ),
    };
  });

  // Transform Education sections
  const educationData: Record<number, any> = {};
  analysis.educations.individualEducationsAnalysis.forEach((edu, index) => {
    educationData[index] = {
      words: {
        spellingMistakes: [
          ...new Set(
            edu.descriptionAnalysis
              .flatMap((point) =>
                point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
              )
              .filter(Boolean)
          ),
        ],
        grammarMistakes: [
          ...new Set(
            edu.descriptionAnalysis
              .flatMap((point) =>
                point.analysis.grammarMistakes.map((m) => m.incorrect)
              )
              .filter(Boolean)
          ),
        ],
        buzzWords: [
          ...new Set(
            edu.descriptionAnalysis
              .flatMap((point) => point.analysis.buzzWords)
              .filter(Boolean)
          ),
        ],
        repeatedWords: (() => {
          const verbs: string[] = edu.descriptionAnalysis.flatMap((d) =>
            d.analysis.actionVerbAnalysis.map((v) => v.verb)
          );
          const counts: Record<string, number> = {};
          verbs.forEach((v) => {
            const key = (v || "").trim().toLowerCase();
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
          });
          return Object.keys(counts)
            .filter((k) => counts[k] > 1)
            .map((k) => k);
        })(),
        personalPronouns: [
          ...new Set(
            edu.descriptionAnalysis
              .flatMap((point) => point.analysis.personalPronouns)
              .filter(Boolean)
          ),
        ],
        actionVerbIssues: [
          ...new Set(
            edu.descriptionAnalysis.flatMap((desc) => {
              return desc.analysis.actionVerbAnalysis
                .filter((verb) => {
                  if (verb.isPastTense !== 0) return false;
                  const statement = desc.statement.trim();
                  const firstWord = statement.split(" ")[0].toLowerCase().replace(/[^\w]/g, "");
                  return firstWord === verb.verb.toLowerCase();
                })
                .map((verb) => verb.verb);
            })
          ),
        ],
      },
      sentences: {
        longSentences: [
          ...new Set(
            edu.descriptionAnalysis
              .filter((desc) => {
                const wordCount = desc.statement.trim().split(/\s+/).length;
                return wordCount > 25;
              })
              .map((desc) => desc.statement)
          ),
        ],
        doesNotEndsWithPeriod: [
          ...new Set(
            edu.descriptionAnalysis
              .filter((desc) => desc.analysis.endsWithPeriod === 0)
              .map((desc) => desc.statement)
          ),
        ],
        repeatedPhrases: [],
        // repeatedPhrases: (() => {
        //   const shingles: string[] = [];
        //   const normalize = (s: string) =>
        //     s
        //       .trim()
        //       .toLowerCase()
        //       .replace(/[^a-z0-9\s]/g, " ")
        //       .replace(/\s+/g, " ");
        //   edu.descriptionAnalysis.forEach((d) => {
        //     const text = normalize(d.statement || "");
        //     if (!text) return;
        //     const words = text.split(" ").filter(Boolean);
        //     const n = 3;
        //     for (let i = 0; i + n <= words.length; i++) {
        //       shingles.push(words.slice(i, i + n).join(" "));
        //     }
        //   });
        //   const counts: Record<string, number> = {};
        //   shingles.forEach((s) => (counts[s] = (counts[s] || 0) + 1));
        //   return Object.keys(counts).filter((k) => counts[k] > 1);
        // })(),
      },
      // hasPassiveVoice: edu.descriptionAnalysis.some(
      //   (desc) => desc.analysis.isPassiveVoice === 1
      // ),
    };
  });

  // Transform Projects sections
  const projectsData: Record<number, any> = {};
  analysis.projects.individualProjectsAnalysis.forEach((proj, index) => {
    projectsData[index] = {
      words: {
        spellingMistakes: [
          ...new Set(
            proj.descriptionAnalysis
              .flatMap((point) =>
                point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
              )
              .filter(Boolean)
          ),
        ],
        grammarMistakes: [
          ...new Set(
            proj.descriptionAnalysis
              .flatMap((point) =>
                point.analysis.grammarMistakes.map((m) => m.incorrect)
              )
              .filter(Boolean)
          ),
        ],
        buzzWords: [
          ...new Set(
            proj.descriptionAnalysis
              .flatMap((point) => point.analysis.buzzWords)
              .filter(Boolean)
          ),
        ],
        repeatedWords: (() => {
          const verbs: string[] = proj.descriptionAnalysis.flatMap((d) =>
            d.analysis.actionVerbAnalysis.map((v) => v.verb)
          );
          const counts: Record<string, number> = {};
          verbs.forEach((v) => {
            const key = (v || "").trim().toLowerCase();
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
          });
          return Object.keys(counts)
            .filter((k) => counts[k] > 1)
            .map((k) => k);
        })(),
        personalPronouns: [
          ...new Set(
            proj.descriptionAnalysis
              .flatMap((point) => point.analysis.personalPronouns)
              .filter(Boolean)
          ),
        ],
        actionVerbIssues: [
          ...new Set(
            proj.descriptionAnalysis.flatMap((desc) => {
              return desc.analysis.actionVerbAnalysis
                .filter((verb) => {
                  if (verb.isPastTense !== 0) return false;
                  const statement = desc.statement.trim();
                  const firstWord = statement.split(" ")[0].toLowerCase().replace(/[^\w]/g, "");
                  return firstWord === verb.verb.toLowerCase();
                })
                .map((verb) => verb.verb);
            })
          ),
        ],
      },
      sentences: {
        longSentences: [
          ...new Set(
            proj.descriptionAnalysis
              .filter((desc) => {
                const wordCount = desc.statement.trim().split(/\s+/).length;
                return wordCount > 25;
              })
              .map((desc) => desc.statement)
          ),
        ],
        doesNotEndsWithPeriod: [
          ...new Set(
            proj.descriptionAnalysis
              .filter((desc) => desc.analysis.endsWithPeriod === 0)
              .map((desc) => desc.statement)
          ),
        ],
        repeatedPhrases : []
        // repeatedPhrases: (() => {
        //   const shingles: string[] = [];
        //   const normalize = (s: string) =>
        //     s
        //       .trim()
        //       .toLowerCase()
        //       .replace(/[^a-z0-9\s]/g, " ")
        //       .replace(/\s+/g, " ");
        //   proj.descriptionAnalysis.forEach((d) => {
        //     const text = normalize(d.statement || "");
        //     if (!text) return;
        //     const words = text.split(" ").filter(Boolean);
        //     const n = 3;
        //     for (let i = 0; i + n <= words.length; i++) {
        //       shingles.push(words.slice(i, i + n).join(" "));
        //     }
        //   });
        //   const counts: Record<string, number> = {};
        //   shingles.forEach((s) => (counts[s] = (counts[s] || 0) + 1));
        //   return Object.keys(counts).filter((k) => counts[k] > 1);
        // })(),
       },
      // hasPassiveVoice: proj.descriptionAnalysis.some(
      //   (desc) => desc.analysis.isPassiveVoice === 1
      // ),
    };
  });

  // Transform Overall/Global mistakes
  const overallData = {
    repeatedActionVerbs: analysis.overallAnalysis.repeatedActionVerbs.verbs || [],
    repeatedActionVerbsWithCounts:
      analysis.overallAnalysis.repeatedActionVerbs.verbsWithCounts || [],
    repeatedSentences: analysis.overallAnalysis.repeatedSentences.phrases || [],
  };

  return {
    profile: profileData,
    experience: experienceData,
    education: educationData,
    projects: projectsData,
    skills: null, // Skills section doesn't have descriptions to analyze
    overall: overallData,
  };
}
